/**
 * AlertsPanel — incidencias recientes como flujo de alertas de la torre.
 * Datos reales de `getAllIncidents` (packages/incidents). Enlaza a la vista
 * completa de incidencias para gestionar.
 */
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Package, ShieldAlert } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { formatRelative } from '../../../../utils/formatters';

const SEV_TONE = {
  baja: 'info',
  media: 'warning',
  alta: 'error',
  critica: 'error',
  low: 'info',
  medium: 'warning',
  high: 'error',
  critical: 'error',
};

function AlertsPanel({ incidents, loading }) {
  const { t } = useTranslation();

  const sevLabel = (s) => {
    const k = (s || '').toLowerCase();
    if (k === 'critica' || k === 'critical') return t('Crítica');
    if (k === 'alta' || k === 'high') return t('Alta');
    if (k === 'media' || k === 'medium') return t('Media');
    if (k === 'baja' || k === 'low') return t('Baja');
    return s || t('Sin severidad');
  };

  return (
    <Card padding="none" className="overflow-hidden flex flex-col">
      <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-status-error-soft text-status-error grid place-items-center">
            <ShieldAlert className="w-4 h-4" strokeWidth={2.2} />
          </span>
          <h3 className="text-sm font-semibold text-fg">{t('Alertas e incidencias recientes')}</h3>
        </div>
        <Link
          to="/admin/incidents"
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        >
          {t('Ver todas')}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="flex-1 min-h-0">
        {loading ? (
          <ul className="divide-y divide-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="px-5 py-3">
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/2" />
              </li>
            ))}
          </ul>
        ) : incidents.length === 0 ? (
          <EmptyState
            icon={<AlertTriangle />}
            title={t('Sin incidencias recientes')}
            description={t('No hay incidencias abiertas en este momento. Todo bajo control.')}
          />
        ) : (
          <ul className="divide-y divide-border">
            {incidents.map((inc) => {
              const tone = SEV_TONE[(inc.severity || '').toLowerCase()] || 'neutral';
              const following = inc.package?.followingNumber;
              return (
                <li key={inc._id || inc.id}>
                  {/* Igual que en el tablero de repartidores: el hover prometía un
                      clic que no existía (clic muerto en Clarity). */}
                  <Link
                    to="/admin/incidents"
                    className="px-5 py-3 flex items-start gap-3 hover:bg-bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                  >
                  <Badge tone={tone} dot className="shrink-0 mt-0.5">
                    {sevLabel(inc.severity)}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-fg truncate">
                      {inc.description || inc.incidentType || t('Incidencia')}
                    </p>
                    <div className="flex items-center gap-2 text-[11px] text-fg-muted mt-0.5">
                      {following && (
                        <span className="inline-flex items-center gap-1">
                          <Package className="w-3 h-3" />#{following}
                        </span>
                      )}
                      {inc.createdAt && <span>{formatRelative(inc.createdAt)}</span>}
                    </div>
                  </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

// memo: `incidents` solo cambia de identidad con datos nuevos del poll de 90s.
// El "hace X min" de cada incidencia se refresca en ese mismo ciclo — suficiente.
export default memo(AlertsPanel);
